import { Box, Card, Icon } from '@zillow/constellation-10';
import { IconChevronRightFilled } from '@zillow/constellation-icons';

import { LoadingParagraph } from '@zg-rentals/loading-components';

import { cardContentStyles, childContentContainerStyles } from './styles';

import type React from 'react';
import type { CardRootPropsInterface } from '@zillow/constellation-10';

type Props = {
  outlined?: boolean;
  bodyHeading?: string;
  body?: string | React.ReactElement;
  withChevron?: boolean;
  children?: Array<React.ReactNode> | React.ReactElement;
  cursor?: 'default' | 'pointer';
  href?: string;
  linkLabel?: string;
  target?: React.HTMLAttributeAnchorTarget;
  onClick?: (event: React.SyntheticEvent) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
};

const SimpleCard = ({
  outlined = true,
  body,
  bodyHeading,
  children,
  cursor: _cursor,
  href,
  linkLabel,
  onClick,
  target,
  withChevron = false,
  isLoading = false,
  isDisabled = false,
  ...rest
}: Props & Omit<CardRootPropsInterface, 'ref'>) => {
  const handleCardClick = (event: React.SyntheticEvent) => {
    if (onClick) {
      onClick(event);
    }
  };

  const isInteractive = Boolean(href || onClick) && !isDisabled && !isLoading;
  const accessibleLinkLabel = linkLabel || bodyHeading || (typeof body === 'string' ? body : undefined);
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;

  return (
    <Card.Root
      data-testid="SimpleCard"
      tone="neutral"
      outlined={outlined}
      elevated={false}
      interactive={isInteractive}
      onClick={isInteractive && !href ? handleCardClick : undefined}
      css={{
        padding: 'none',
      }}
      {...rest}
    >
      {isInteractive && href && (
        <Card.BlockAnchor
          href={href}
          target={target}
          rel={rel}
          onClick={handleCardClick}
          aria-hidden={undefined}
          aria-label={accessibleLinkLabel}
          tabIndex={0}
        />
      )}
      <Card.Body css={cardContentStyles}>
        <Box css={{ ...childContentContainerStyles, marginRight: withChevron ? '400' : undefined }}>
          {children ? (
            children
          ) : (
            <>
              <LoadingParagraph
                data-testid="SimpleCard-Heading"
                textStyle="body-sm-bold"
                isLoading={isLoading}
                loadingWidth="100%"
                css={{ fontSize: { lg: 'body' } }}
              >
                {bodyHeading}
              </LoadingParagraph>

              <LoadingParagraph
                data-testid="SimpleCard-Body"
                textStyle="body-sm"
                isLoading={isLoading}
                loadingWidth="75%"
              >
                {body}
              </LoadingParagraph>
            </>
          )}
        </Box>

        {withChevron && (
          <Box css={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
            <Icon>
              <IconChevronRightFilled />
            </Icon>
          </Box>
        )}
      </Card.Body>
    </Card.Root>
  );
};

export default SimpleCard;
